import { useState, useEffect } from "react";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, Search, Activity, Eye, Calendar } from "lucide-react";

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total_logs: 0,
    logs_today: 0,
    logs_this_week: 0,
    most_active_user: { email: null, count: 0 },
    most_common_action: { action: null, count: 0 }
  });
  const [actionTypes, setActionTypes] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Filters
  const [actionFilter, setActionFilter] = useState("");
  const [actorEmailFilter, setActorEmailFilter] = useState("");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    loadStats();
    loadActionTypes();
    loadResourceTypes();
    loadRecentActivity();
  }, [page, actionFilter, actorEmailFilter, resourceTypeFilter, startDate, endDate]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (actionFilter) params.action = actionFilter;
      if (actorEmailFilter) params.actor_email = actorEmailFilter;
      if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();

      const res = await API.get("/audit-logs/logs", { params });
      setLogs(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      toast.error("Failed to load audit logs");
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await API.get("/audit-logs/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const loadActionTypes = async () => {
    try {
      const res = await API.get("/audit-logs/action-types");
      setActionTypes(res.data.action_types);
    } catch (err) {
      console.error("Failed to load action types:", err);
    }
  };

  const loadResourceTypes = async () => {
    try {
      const res = await API.get("/audit-logs/resource-types");
      setResourceTypes(res.data.resource_types);
    } catch (err) {
      console.error("Failed to load resource types:", err);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const res = await API.get("/audit-logs/recent-activity", {
        params: { limit: 10 }
      });
      setRecentActivity(res.data.recent_activity);
    } catch (err) {
      console.error("Failed to load recent activity:", err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (actorEmailFilter) params.actor_email = actorEmailFilter;
      if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();

      const res = await API.get("/audit-logs/logs/export/csv", {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Audit logs exported successfully");
    } catch (err) {
      toast.error("Failed to export audit logs");
    }
  };

  const viewLogDetail = async (logId) => {
    try {
      const res = await API.get(`/audit-logs/logs/${logId}`);
      setSelectedLog(res.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error("Failed to load log details");
    }
  };

  const clearFilters = () => {
    setActionFilter("");
    setActorEmailFilter("");
    setResourceTypeFilter("");
    setStartDate("");
    setEndDate("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getActionBadgeVariant = (action) => {
    if (action.includes("created") || action.includes("unlocked")) return "default";
    if (action.includes("deleted") || action.includes("locked")) return "destructive";
    if (action.includes("updated")) return "secondary";
    return "outline";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="w-8 h-8" />
            Audit Logs
          </h1>
          <p className="text-gray-600 mt-1">View system activity and security events</p>
        </div>
        <Button onClick={handleExportCSV}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_logs}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.logs_today}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats.logs_this_week}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Most Active User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-semibold truncate" title={stats.most_active_user.email}>
              {stats.most_active_user.email || "N/A"}
            </div>
            <div className="text-xs text-gray-500">{stats.most_active_user.count} actions</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Top Action</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-semibold truncate" title={stats.most_common_action.action}>
              {stats.most_common_action.action || "N/A"}
            </div>
            <div className="text-xs text-gray-500">{stats.most_common_action.count} times</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="text-gray-500 text-sm">No recent activity</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((log) => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div className="flex items-center gap-3">
                    <Badge variant={getActionBadgeVariant(log.action)}>
                      {log.action}
                    </Badge>
                    <span className="text-sm font-medium">{log.actor_email}</span>
                    {log.resource_type && (
                      <span className="text-sm text-gray-600">on {log.resource_type}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Action Type</Label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              >
                <option value="">All Actions</option>
                {actionTypes.map((at) => (
                  <option key={at.action} value={at.action}>
                    {at.action} ({at.count})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Resource Type</Label>
              <select
                value={resourceTypeFilter}
                onChange={(e) => setResourceTypeFilter(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              >
                <option value="">All Resources</option>
                {resourceTypes.map((rt) => (
                  <option key={rt.resource_type} value={rt.resource_type}>
                    {rt.resource_type} ({rt.count})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Actor Email</Label>
              <Input
                type="text"
                placeholder="Search by email..."
                value={actorEmailFilter}
                onChange={(e) => setActorEmailFilter(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium">Timestamp</th>
                  <th className="text-left p-4 font-medium">Actor</th>
                  <th className="text-left p-4 font-medium">Action</th>
                  <th className="text-left p-4 font-medium">Resource</th>
                  <th className="text-left p-4 font-medium">IP Address</th>
                  <th className="text-right p-4 font-medium">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <>
                    <style>{`@keyframes al-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
                    {[...Array(6)].map((_, i) => (
                      <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td className="p-4"><div style={{height:12,width:110,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:12,width:90,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:12,width:120,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:12,width:80,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:12,width:90,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4 text-right"><div style={{height:12,width:40,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'al-shimmer 1.4s ease infinite',marginLeft:'auto'}} /></td>
                      </tr>
                    ))}
                  </>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <svg width="72" height="64" viewBox="0 0 72 64" fill="none" className="mx-auto mb-4 opacity-70">
                        <rect x="8" y="8" width="56" height="48" rx="6" fill="#F1F5F9" stroke="#CBD5E1" strokeWidth="1.5"/>
                        <rect x="18" y="20" width="36" height="5" rx="2.5" fill="#CBD5E1"/>
                        <rect x="18" y="30" width="28" height="4" rx="2" fill="#E2E8F0"/>
                        <rect x="18" y="39" width="20" height="4" rx="2" fill="#E2E8F0"/>
                        <circle cx="58" cy="54" r="12" fill="#6366F1"/>
                        <path d="M53 54h10M58 49v10" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                      </svg>
                      <p className="text-slate-600 font-semibold text-sm">No audit logs found</p>
                      <p className="text-slate-400 text-xs mt-1">Try adjusting your filters or date range</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="p-4 text-sm">{formatDate(log.timestamp)}</td>
                      <td className="p-4">
                        <div>
                          <div className="text-sm font-medium">{log.actor_email || "System"}</div>
                          {log.actor_role && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {log.actor_role}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {log.resource_type ? (
                          <div className="text-sm">
                            <div className="font-medium">{log.resource_type}</div>
                            {log.resource_id && (
                              <div className="text-xs text-gray-500 truncate" style={{ maxWidth: '150px' }}>
                                {log.resource_id}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {log.ip_address || "-"}
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewLogDetail(log.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2">Page {page} of {Math.ceil(total / 50)}</span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 50)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Log Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Audit Log Details
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Timestamp</Label>
                  <p className="text-sm font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>

                <div>
                  <Label className="text-gray-600">Action</Label>
                  <div className="mt-1">
                    <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                      {selectedLog.action}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-600">Actor Email</Label>
                  <p className="text-sm font-medium">{selectedLog.actor_email || "System"}</p>
                </div>

                <div>
                  <Label className="text-gray-600">Actor Role</Label>
                  <p className="text-sm font-medium">{selectedLog.actor_role || "-"}</p>
                </div>

                <div>
                  <Label className="text-gray-600">Resource Type</Label>
                  <p className="text-sm font-medium">{selectedLog.resource_type || "-"}</p>
                </div>

                <div>
                  <Label className="text-gray-600">Resource ID</Label>
                  <p className="text-sm font-medium truncate" title={selectedLog.resource_id}>
                    {selectedLog.resource_id || "-"}
                  </p>
                </div>

                <div>
                  <Label className="text-gray-600">IP Address</Label>
                  <p className="text-sm font-medium">{selectedLog.ip_address || "-"}</p>
                </div>

                <div>
                  <Label className="text-gray-600">Log ID</Label>
                  <p className="text-xs text-gray-500 truncate" title={selectedLog.id}>
                    {selectedLog.id}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">User Agent</Label>
                <p className="text-xs text-gray-600 mt-1 break-all">
                  {selectedLog.user_agent || "-"}
                </p>
              </div>

              <div>
                <Label className="text-gray-600">Details</Label>
                <div className="space-y-2 max-h-96 overflow-y-auto mt-2">
                  {Object.entries(selectedLog.details || {}).map(([k, v]) => (
                    <div key={k} className="flex gap-3 text-sm">
                      <span className="font-semibold text-slate-600 min-w-[140px] flex-shrink-0 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="text-slate-800 font-mono text-xs bg-slate-50 px-2 py-0.5 rounded break-all">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
                      </span>
                    </div>
                  ))}
                  {(!selectedLog.details || Object.keys(selectedLog.details).length === 0) && (
                    <p className="text-slate-400 text-sm">No additional details</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
